/**
 * Sign-up. Since plan 024 / ADR-0009 registration no longer creates an
 * organization inline — the org is chosen (or joined, for an invitee) after the
 * verification email is clicked. So this page has two jobs beyond collecting
 * credentials:
 *
 *  1. Send `full_name`, which the backend now requires.
 *  2. Carry an invitation through. An invitee arrives from `/invitation/:token`
 *     as `/register?next=/invitation/<token>` (or `?invite=<token>` for a direct
 *     link). We pass the token to register() so the backend can validate it up
 *     front and perform the org-join at verification time, and we forward `next`
 *     to /verify-email so the destination survives the waiting screen:
 *     Register → VerifyEmail → Login → Login's post-login `next` redirect.
 *
 * The invitation metadata read is the same public, side-effect-free endpoint
 * Invitation.tsx uses, on the same query key — the two pages share one cache
 * entry. It's decoration only: register() re-validates the token server-side, so
 * a failed lookup hides the banner and never blocks the form.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { toast } from 'sonner'
import { CheckCircle2, Loader2 } from 'lucide-react'
// Registration stays on the axios client from `@/lib/api`: there is no
// `features/auth` module, and the single-flight refresh-token queue lives in
// this instance's interceptors — routing one page through `lib/http.ts` would
// bypass it and add a second auth client for no gain (rules.md §3.2).
import api from '@/lib/api'
import { apiClient } from '@/utils/api'
import { useApi } from '@/hooks/useApi'
import { qk } from '@/lib/queryKeys'

const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong']
const STRENGTH_COLORS = ['bg-destructive', 'bg-destructive', 'bg-warning', 'bg-info', 'bg-success']

const schema = z.object({
  full_name: z.string().trim().min(1, 'Enter your name'),
  email: z.string().trim().min(1, 'Enter your email').email('Enter a valid email address'),
  password: z.string().min(8, 'Use at least 8 characters'),
})

type FormValues = z.infer<typeof schema>

/** `/invitation/<token>` → `<token>`; anything else → ''. */
function tokenFromNext(next: string): string {
  return /^\/invitation\/([^/?#]+)/.exec(next)?.[1] ?? ''
}

export default function Register() {
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)

  const { next, inviteToken } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const nextParam = params.get('next') || ''
    return {
      next: nextParam,
      // `?invite=` is the direct-link alternative to routing through `next=`.
      inviteToken: params.get('invite') || tokenFromNext(nextParam),
    }
  }, [location.search])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { full_name: '', email: '', password: '' },
  })

  const { data: invite } = useApi(
    () => apiClient.getInvitation(inviteToken),
    [inviteToken],
    {
      skip: !inviteToken,
      queryKey: inviteToken ? qk.invitation(inviteToken) : ['invitation', 'none'],
      onSuccess: (info) => {
        // Prefill the invited address, but leave it editable — the backend
        // re-checks the email match at register time either way. Only fills an
        // untouched field so it can't clobber what someone is typing.
        if (info.email && !form.getValues('email')) form.setValue('email', info.email)
      },
    },
  )

  const password = form.watch('password')

  const score = useMemo(() => {
    if (!password) return 0
    if (password.length < 8) return 0
    let s = 1
    if (password.length >= 12) s++
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s++
    if (/[0-9]/.test(password)) s++
    if (/[^A-Za-z0-9]/.test(password)) s++
    return Math.min(s, 4)
  }, [password])

  /** `?email=…&next=…`, skipping either part when it's empty. */
  const authQuery = (email: string) => {
    const qs = new URLSearchParams()
    if (email) qs.set('email', email)
    if (next) qs.set('next', next)
    const s = qs.toString()
    return s ? `?${s}` : ''
  }

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)
    try {
      const { data } = await api.post('/auth/register', {
        full_name: values.full_name,
        email: values.email,
        password: values.password,
        // Omitted entirely when absent — the backend treats a present token as
        // one it must validate.
        ...(inviteToken ? { invite_token: inviteToken } : {}),
      })
      if (data.email_sent === false) {
        toast.error(data.message || 'Account created, but we could not send the verification email.')
      } else {
        toast.success('Account created! Check your email to verify.')
      }
      navigate(`/verify-email${authQuery(values.email)}`)
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('That email is already registered.', {
          action: { label: 'Log in', onClick: () => navigate(`/login${authQuery(values.email)}`) },
        })
      } else {
        // Covers the 400s too — an invalid/expired/mismatched invite token, or a
        // missing name that somehow slipped past zod. The server phrases these
        // better than a client-side guess would.
        toast.error(error?.response?.data?.error || 'Failed to create account')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const invited = !!invite
  // Sentence-initial position — the mid-sentence banner below uses 'a team'.
  const teamName = invite?.team_name || 'Your team'

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gold-gradient p-12 text-white lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-white/20 backdrop-blur" />
          <span className="text-xl font-semibold">Granula</span>
        </div>
        <div className="relative z-10">
          <h1 className="max-w-sm text-4xl font-semibold leading-tight">
            {invited ? `${teamName} is waiting for you.` : 'Get your team set up in minutes.'}
          </h1>
          <p className="mt-4 max-w-md text-white/80">
            One workspace for projects, tasks, leave and expenses — with approvals and roles built in.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              // An invitee isn't starting a workspace — they're joining one — so
              // the "free to start" line is swapped rather than shown.
              invited ? 'Your teammates are already set up' : 'Free to start, no card required',
              'Invite your team and assign roles',
              'Approvals for leave & spending',
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-white/90">
                <CheckCircle2 className="h-5 w-5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 text-sm text-white/60">© Granula · Work, organised.</p>
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-black/10 blur-3xl" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="h-8 w-8 rounded-full bg-gold-gradient" />
            <span className="text-xl font-semibold">Granula</span>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight">Create your account</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to={`/login${authQuery('')}`} className="font-medium text-gold hover:underline">Log in</Link>
          </p>

          {invite && (
            <p className="mt-5 rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-foreground">
              You're joining <span className="font-medium">{invite.team_name || 'a team'}</span>. Create your
              account and verify your email to accept the invitation.
            </p>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={() => (window.location.href = '/api/auth/oauth/google/start')}
            className="mt-6 w-full"
          >
            Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>

          <Form {...form}>
            {/* noValidate: zod is the single source of validation truth (rules.md
                §6) — native constraint bubbles would preempt the field messages. */}
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-4">
              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full name</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="name" placeholder="Ada Lovelace" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" autoComplete="email" placeholder="name@example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        autoComplete="new-password"
                        placeholder="At least 8 characters"
                      />
                    </FormControl>
                    {password && (
                      <div className="flex items-center gap-2 pt-1">
                        <div className="flex flex-1 gap-1">
                          {[0, 1, 2, 3].map((i) => (
                            <div key={i} className={`h-1.5 flex-1 rounded-full ${i < score ? STRENGTH_COLORS[score] : 'bg-black/10'}`} />
                          ))}
                        </div>
                        <span className="w-16 text-right text-xs text-muted-foreground">{STRENGTH_LABELS[score]}</span>
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…</> : 'Create account'}
              </Button>
            </form>
          </Form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            By creating an account you agree to our{' '}
            <Link to="/" className="text-gold hover:underline">Terms &amp; Conditions</Link>.
          </p>
        </div>
      </div>
    </div>
  )
}

export const Component = Register
